// Bare specifier (not "./smartyFetch") so the Storybook webpack alias in
// `.storybook/main.js` matches and swaps in MockSmartyFetch. Relative
// imports resolve to absolute paths before alias rules run, missing the
// swap. Jest's `jest.mock` in setupTests.js matches either form.
import { fetchSmartyLookup } from "Utilities/Address/AddressValidation/providers/smarty/smartyFetch";

/**
 * Smarty US Autocomplete Pro client.
 *
 * The raw HTTP call lives in `smartyFetch.js` so tests + Storybook can
 * alias that single file to `testUtils/MockSmartyFetch` and exercise this
 * mapper end-to-end against realistic Smarty fixtures. Keep the network
 * call out of this file.
 *
 * `search` carries the STREET *plus the user's typed CITY* (composed here as
 * "<street> <city>"). Smarty's autocomplete prefix-matches a trailing city
 * token, so this is what lets a USPS-mailable ALTERNATE city verify — the
 * street alone only returns the preferred-city row (e.g. ZIP 63021 returns
 * "Ballwin", never the mailable alternate "Manchester", until the typed city
 * rides along). The MIN_QUERY_LENGTH gate stays on the street only.
 */

// Smarty's autocomplete cannot resolve a partial street from one or two
// characters — every sub-3-char query is a guaranteed no-op API hit, and
// each call is metered. Short-circuit here to avoid paying for keystrokes
// the user is still in the middle of typing.
const MIN_QUERY_LENGTH = 3;

/**
 * Sentinel inserted into a candidate's addr1 when Smarty signals the
 * address is incomplete (`entries > 1` — a multi-unit building). The
 * sentinel appears in shadow text and panel rows so the user can see
 * what's missing; after autofill the consuming hook focuses the addr1
 * input and selects the sentinel so the user's first keystroke
 * replaces it with their actual unit number.
 *
 * Three underscores — they read as a *blank to be filled in* for the unit
 * number, sitting directly against a partial one (`Fl 5___`, see
 * `appendUnitSlot`). Detection keys off `addr1.includes(UNIT_PLACEHOLDER)` and
 * the slot-select keys off `UNIT_PLACEHOLDER.length`, so the glyph is the
 * single source of truth — its width can change here without touching anything
 * that detects, selects, or strips it.
 */
export const UNIT_PLACEHOLDER = "___";

// Attach the unit-fill slot to a multi-unit `base`. The separator depends on
// whether `secondary` is a *partial number* or a *bare designator*:
//   - "Fl 5" ends in a digit → the slot is the rest of that number, so it
//     attaches with no space ("1 World Trade Ctr Fl 5___"). The next keystroke
//     extends the number ("Fl 5" → "Fl 57"), matching Smarty's grouping.
//   - "Apt" / "Ste" (bare designator) → the slot is a fresh number, space-
//     separated ("1 Pennsylvania Pl Apt ___").
const appendUnitSlot = (base, secondary) =>
  /\d$/.test(secondary || "")
    ? `${base}${UNIT_PLACEHOLDER}`
    : `${base} ${UNIT_PLACEHOLDER}`;

/**
 * @typedef {import("../../types").AddressInput & {
 *   status: import("../../types").VerdictStatus,
 *   confidence: number,
 *   entries?: number,
 * }} CandidateAddress
 *
 * `entries` mirrors Smarty's field of the same name:
 *   - 0 — single-address record (no sub-units; most homes)
 *   - 1 — one specific unit
 *   - > 1 — multi-unit building; `secondary` is a placeholder like "Apt"
 *     and the user must drill in (type a unit number) before this becomes
 *     a real address. The autocomplete consumer suppresses the Accept chip
 *     and group-blur autofill in this case.
 */

/**
 * Fetches autocomplete suggestions from Smarty for a partial US street.
 *
 * Returns `[]` for the no-results cases the caller treats as "no suggestions":
 * non-US country, a too-short street, or a benign empty body (no key / not
 * live / aborted, where `fetchSmartyLookup` returns null).
 *
 * A real Smarty FAILURE (4xx/5xx/network/parse) is NOT collapsed to `[]` here
 * — `fetchSmartyLookup` throws, and that throw propagates so react-query sees
 * `isError` and the engine can keep the flow invisible (no spurious
 * unverifiable verdict). Smarty itself reports the failure to Sentry; we don't
 * swallow it into the suggestion list.
 *
 * @param {string} query - Partial street text from the addr1 field.
 * @param {string} [country] - ISO 3166-1 alpha-2. Only "US" is supported.
 * @param {{ signal?: AbortSignal, city?: string }} [options] - `city` is the
 *   user's typed city, composed onto the end of `search` (see below).
 * @returns {Promise<CandidateAddress[]>}
 */
export const fetchSmartyAutocomplete = async (
  query,
  country = "US",
  options = {}
) => {
  if (country !== "US") return [];
  // Strip the multi-unit slot sentinel before querying. After an autofill the
  // addr1 carries "…Fl ___", but the "___" is a UI affordance (the unit number the
  // user hasn't typed yet), not part of any real address — Smarty returns
  // nothing for it. Remove it (and the trailing space it leaves) so the live
  // re-fetch queries the real partial street and keeps the candidate set warm.
  const trimmed = (query || "").split(UNIT_PLACEHOLDER).join("").trim();
  // The MIN_QUERY_LENGTH gate is on the STREET only — a long typed city must
  // not mask a too-short street (and city-only is meaningless without a street).
  if (trimmed.length < MIN_QUERY_LENGTH) return [];

  // Compose the typed city onto the end of the street as one `search` string.
  // Smarty's autocomplete prefix-matches a trailing city token, so this is what
  // lets a USPS-mailable ALTERNATE city (e.g. "Manchester" for a ZIP whose
  // preferred city is "Ballwin") surface as a candidate — the street alone only
  // returns the preferred-city row.
  const city = (options.city || "").trim();
  const search = city ? `${trimmed} ${city}` : trimmed;

  const body = await fetchSmartyLookup(search, { ...options, country });
  const suggestions = Array.isArray(body?.suggestions) ? body.suggestions : [];
  return suggestions.map(suggestionToCandidate).filter(Boolean);
};

/**
 * Maps a Smarty suggestion object to a CandidateAddress.
 *
 * Smarty's autocomplete endpoint does not return a deliverability verdict —
 * every suggestion is a plausible-but-unverified completion. We mark them
 * as `"unconfirmed"`; the verify-on-group-blur path is what flips an
 * address to `"confirmed"`.
 *
 * @param {Object} s
 * @returns {CandidateAddress | null}
 */
const suggestionToCandidate = (s) => {
  if (!s || typeof s !== "object") return null;
  const street = s.street_line || "";
  if (!street) return null;
  const entries = typeof s.entries === "number" ? s.entries : 0;
  const base = s.secondary ? `${street} ${s.secondary}`.trim() : street;
  return {
    // Multi-unit building (entries > 1): append the placeholder so it
    // rides through every consumer (shadow text, panel rows, autofill).
    // The hook treats addresses containing this sentinel as "not yet
    // verifiable" and focuses/selects the slot post-autofill.
    addr1: entries > 1 ? appendUnitSlot(base, s.secondary) : base,
    // Smarty's bare street_line (no secondary, no UNIT_PLACEHOLDER). For a
    // multi-unit building (entries > 1) the placeholder'd `addr1` can't be
    // reversed back to the bare street (the "___" may attach with no space and the
    // secondary can be multi-token), so the bare-building (no-unit) commit reads
    // the street straight off this field. See useAddressAutocomplete's
    // bare-building branch + buildBareBuildingCommit.
    streetLine: street,
    city: s.city || "",
    state: s.state || "",
    zip: s.zipcode || "",
    country: "US",
    status: "unconfirmed",
    confidence: 0,
    entries,
  };
};
